import {
  ReactElement, useCallback, useEffect, useRef, useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Loader } from '~/components';
import { apiCall } from '~/providers';
import { FullProg_v4 } from '~/providers/types';
import { DamEcoSimplifiedConfigResponse } from '~/providers/types/api-private';
import { EcoSimplifiedStatusHeader } from './components/EcoSimplifiedStatusHeader';
import { getApiErrorDetail } from './apiError';
import { toDamEcoSimplifiedFormValues, toDamEcoSimplifiedSaveParams, toSelfReferenceEnvironmentOptions } from './helpers';
import { DamEcoSimplifiedForm } from './DamEcoSimplifiedForm';
import { LoaderWrapper, ReadOnlyArea, ReadOnlyNotice } from './styles';
import { SecondaryButton } from './ds';
import {
  DamEcoModeDeviceInput, DamEcoModeLegacyScreen, DamEcoSimplifiedFormValues, DamSelfReferenceEnvironmentOption,
} from './types';

const EMPTY_PROGRAMMING: FullProg_v4 = { week: {}, exceptions: {} };

interface DamEcoModeAutomationProps {
  damId: string;
  device: DamEcoModeDeviceInput;
  legacy: DamEcoModeLegacyScreen;
  /**
   * Se a aba Automação está aberta. O componente fica montado nas outras abas (sem renderizar nada) para
   * não perder as edições nem recarregar a configuração a cada troca de aba.
   */
  visible: boolean;
}

/**
 * Aba Automação do DAM com o Modo Eco: em DAM sem suporte ao modo simplificado mostra só o conteúdo
 * atual; em DAM suportado mostra só a tela do Modo Eco simplificado, o único que o firmware aceita.
 */
export function DamEcoModeAutomation({
  damId,
  device,
  legacy,
  visible,
}: Readonly<DamEcoModeAutomationProps>): ReactElement {
  const { t } = useTranslation();
  const [ecoMode, setEcoMode] = useState<DamEcoSimplifiedConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isProgrammingLoaded, setIsProgrammingLoaded] = useState(false);
  const [environmentOptions, setEnvironmentOptions] = useState<DamSelfReferenceEnvironmentOption[]>([]);
  const methods = useForm<DamEcoSimplifiedFormValues>({ mode: 'onSubmit', reValidateMode: 'onChange' });
  const { reset, formState } = methods;

  // Lidos no render para o react-hook-form acompanhar quais campos mudaram.
  const isProgrammingDirty = !!formState.dirtyFields.programming;
  const isReferenceDutDirty = !!formState.dirtyFields.referenceDutId;
  const isSelfReferenceDirty = !!formState.dirtyFields.selfReference;
  const isReferenceEnvironmentDirty = !!formState.dirtyFields.referenceEnvironmentId;
  const isTitlesDirty = !!formState.dirtyFields.programmingTitles;
  const { clientId, unitId, deviceId } = device.monitoringScope;
  const formDevice = {
    ...device,
    canManageSchedule: isProgrammingLoaded && device.canManageSchedule,
    selfReferenceEnvironmentOptions: environmentOptions,
  };

  /** A programação é a mesma do modo atual, lida da mesma rota; sem ela, a lista fica só para leitura. */
  const loadProgramming = useCallback(async (): Promise<FullProg_v4 | null> => {
    try {
      return await apiCall('/dam/get-programming-v3', { damId });
    } catch (error) {
      console.error(error);
      toast.error(t('ecoSimplificadoFalhaCarregarProgramacao'));
      return null;
    }
  }, [damId, t]);

  /**
   * Ambientes do sensor próprio, da mesma rota da tela do modo atual. Lá a lista só é carregada para DAM3;
   * aqui vale o suporte ao modo simplificado, que depende do firmware e não do prefixo.
   */
  const loadEnvironments = useCallback(async (): Promise<DamSelfReferenceEnvironmentOption[]> => {
    if (!clientId || !unitId) return [];
    try {
      const { environments } = await apiCall('/environments/get-environment-list', { CLIENT_ID: clientId, UNIT_ID: unitId, CHECK_DAMS_MONITORING: true });
      return toSelfReferenceEnvironmentOptions(environments, deviceId);
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [clientId, unitId, deviceId]);

  const loadEcoMode = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await apiCall('/dam/get-eco-simplified-config', { DAM_ID: damId });
      // Programação e ambientes só servem à tela simplificada: DAM sem suporte não os carrega.
      const [programming, environments] = response.SUPPORTED
        ? await Promise.all([loadProgramming(), loadEnvironments()])
        : [null, []];
      setIsProgrammingLoaded(!!programming);
      setEnvironmentOptions(environments);
      setEcoMode(response);
      setLoadFailed(false);
      reset(toDamEcoSimplifiedFormValues(response.CONFIG, programming ?? EMPTY_PROGRAMMING, response.PROGRAMMING_TITLES));
    } catch (error) {
      // Sem a leitura não dá para saber se o firmware suporta o modo simplificado: a tela atual fica só
      // para leitura até recarregar, para não gravar configuração do eco legado num DAM que só aceita o simplificado.
      console.error(error);
      setEcoMode(null);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [damId, reset, loadProgramming, loadEnvironments]);

  async function onSave(values: DamEcoSimplifiedFormValues): Promise<void> {
    setIsSaving(true);
    try {
      const response = await apiCall('/dam/set-eco-simplified-config', toDamEcoSimplifiedSaveParams(damId, values, {
        programming: isProgrammingDirty,
        referenceDut: isReferenceDutDirty,
        selfReference: isSelfReferenceDirty,
        referenceEnvironment: isReferenceEnvironmentDirty,
        // Os títulos são identificados pelo grupo de dias e período, então acompanham mudanças na programação.
        programmingTitles: isTitlesDirty || isProgrammingDirty,
      }));
      setEcoMode(response);
      reset(toDamEcoSimplifiedFormValues(response.CONFIG, values.programming, response.PROGRAMMING_TITLES));
      toast.success(t('ecoSimplificadoConfiguracaoSalva'));
      legacy.onRefresh();
    } catch (error) {
      // Os valores editados ficam no formulário para o usuário tentar de novo.
      const detail = getApiErrorDetail(error);
      toast.error(detail ? `${t('ecoSimplificadoFalhaSalvar')} ${detail}` : t('ecoSimplificadoFalhaSalvar'));
    } finally {
      setIsSaving(false);
    }
  }

  function onDiscard(): void {
    reset();
  }

  // Carrega uma vez por DAM, na primeira vez que a aba é aberta: as funções de carga mudam de identidade
  // a cada atualização da página (e o StrictMode repete efeitos no desenvolvimento), o que repetia as
  // chamadas. Depois de salvar, a tela usa a resposta da própria gravação.
  const loadEcoModeRef = useRef(loadEcoMode);
  loadEcoModeRef.current = loadEcoMode;
  const loadedDamIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!visible || loadedDamIdRef.current === damId) return;
    loadedDamIdRef.current = damId;
    loadEcoModeRef.current();
  }, [damId, visible]);

  if (!visible) return <></>;

  if (isLoading) {
    return (
      <LoaderWrapper>
        <Loader variant="primary" />
      </LoaderWrapper>
    );
  }

  if (loadFailed) {
    return (
      <>
        <ReadOnlyNotice data-testid="eco-modo-falha-carregar">
          {t('ecoSimplificadoFalhaCarregarConfig')}
          {' '}
          <SecondaryButton type="button" onClick={() => loadEcoMode()} data-testid="eco-modo-tentar-novamente">
            {t('ecoSimplificadoTentarNovamente')}
          </SecondaryButton>
        </ReadOnlyNotice>
        <ReadOnlyArea disabled aria-disabled>
          {legacy.render(true)}
        </ReadOnlyArea>
      </>
    );
  }

  if (!ecoMode?.SUPPORTED) {
    return <>{legacy.render(false)}</>;
  }

  return (
    <>
      <EcoSimplifiedStatusHeader online={device.isOnline} />
      <FormProvider {...methods}>
        <DamEcoSimplifiedForm
          damId={damId}
          device={formDevice}
          isSaving={isSaving}
          onSave={onSave}
          onDiscard={onDiscard}
        />
      </FormProvider>
    </>
  );
}
